"use client";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { formatDistanceToNow } from "date-fns";
import { Bell, Check, CheckCheck, Circle } from "lucide-react";

import type { iNotification } from "@/@sdk/notifications/notifications.models";
import { SDK } from "@/@sdk/sdk";
import { NotificationTypeIcon } from "@/components/notifications/notification-type-icon";
import { NOTIFICATIONS_CHANGED_EVENT } from "@/components/notifications/use-notification-realtime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const POLL_INTERVAL_MS = 60_000;
const LIST_LIMIT = 20;

export function NotificationBell({
  className,
  align = "end",
}: {
  className?: string;
  align?: "start" | "center" | "end";
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<iNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const result = await SDK.notifications.unreadCount();
      setUnreadCount(result.unreadCount ?? 0);
    } catch {
      // Ignore transient unread-count failures.
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await SDK.notifications.query({
        options: { page: 1, limit: LIST_LIMIT, sort: { createdAt: -1 } },
      });
      setNotifications(result.docs ?? []);
      setUnreadCount(result.unreadCount ?? 0);
    } catch {
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const interval = window.setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    const onNotificationsChanged = () => {
      void refreshUnreadCount();
    };
    window.addEventListener(
      NOTIFICATIONS_CHANGED_EVENT,
      onNotificationsChanged
    );
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(
        NOTIFICATIONS_CHANGED_EVENT,
        onNotificationsChanged
      );
    };
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  const handleMarkRead = async (
    notification: iNotification,
    isRead: boolean,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation();
    if (!notification.id) return;
    try {
      const updated = await SDK.notifications.markRead(notification.id, isRead);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === updated.id ? { ...item, ...updated } : item
        )
      );
      await refreshUnreadCount();
    } catch {
      // Keep list as-is on failure.
    }
  };

  const handleMarkAllRead = async () => {
    setIsMarkingAll(true);
    try {
      await SDK.notifications.markAllRead();
      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          isRead: true,
          readAt: item.readAt ?? Date.now(),
        }))
      );
      setUnreadCount(0);
    } catch {
      // Ignore.
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleClickNotification = async (notification: iNotification) => {
    if (!notification.isRead) {
      await handleMarkRead(notification, true);
    }
    setOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const badgeLabel =
    unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
        >
          <Bell className="h-5 w-5" />
          {badgeLabel && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px]"
            >
              {badgeLabel}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 sm:w-96" align={align}>
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 px-2 text-xs"
            onClick={handleMarkAllRead}
            disabled={isMarkingAll || unreadCount === 0}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Mark all as read
          </Button>
        </div>
        <ScrollArea className="h-[360px]">
          {isLoading ? (
            <div className="space-y-3 p-4">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <ul className="divide-y">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60",
                      !notification.isRead && "bg-muted/40"
                    )}
                    onClick={() => handleClickNotification(notification)}
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                      <NotificationTypeIcon type={notification.type} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            "truncate text-sm",
                            !notification.isRead && "font-semibold"
                          )}
                        >
                          {notification.title}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          title={
                            notification.isRead
                              ? "Mark as unread"
                              : "Mark as read"
                          }
                          onClick={(event) =>
                            handleMarkRead(
                              notification,
                              !notification.isRead,
                              event
                            )
                          }
                        >
                          {notification.isRead ? (
                            <Circle className="h-3.5 w-3.5" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {notification.body}
                      </p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {formatDistanceToNow(notification.createdAt, {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
